import type {
  Entity,
  Filters,
  FilterByID,
  MetricsType,
  FiltersOptions,
  SelectedFilters,
  SelectedFilterColumnsData,
  SavedDashboardFiltersList,
  SavedDashboardFiltersListData,
  CurrentJourneyAndTouchpointBreadCrumbData,
  SelectedDashboardEntityFilters,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {AnalyticsJourneysOverviewActions} from 'src/action-creators/analytics/analytics-journeys-overview';

import {
  SET_SELECTED_FILTERS,
  SET_SELECTED_ENTITIES,
  SET_SELECTED_JOURNEYS,
  SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  SET_SELECTED_METRICS_TYPE,
  SET_CURRENT_JOURNEY_AND_TOUCHPOINT_DATA,
  SET_CHART_FREQUENCY,
  RECEIVE_FILTERS_OPTIONS,
  RECEIVE_SENT_FROM_OPTIONS,
  RECEIVE_JOURNEY_OPTIONS,
  RECEIVE_JOURNEY_TABLE_COLUMNS,
  RECEIVE_TOUCHPOINT_TABLE_COLUMNS,
  RECEIVE_PEOPLE_TABLE_COLUMNS,
  RECEIVE_SAVED_DASHBOARDS,
  SET_CURRENT_DASHBOARD,
  CLEAR_SELECTED_FILTERS,
  SET_LOADING_DASHBOARD_FILTERS,
  SET_SELECTED_DASHBOARD_ENTITY_FILTERS,
  SET_NAVIGATION_DOWN,
  SET_NAVIGATION_UP,
  SET_ALL_JOURNEYS_ENTITIES,
  SET_ALL_TOUCHPOINTS_ENTITIES,
  SET_ALL_PEOPLE_ENTITIES,
  SET_ALL_JOURNEYS_FILTERS,
  SET_ALL_PEOPLE_FILTERS,
  SET_ALL_TOUCHPOINTS_FILTERS,
  SET_SELECTED_DATES,
  SET_ENTITIES,
  SET_FILTERS,
} from 'src/action-creators/analytics/analytics-journeys-overview';


export type AnalyticsJourneysOverviewState = {
  selectedFilters: {
    [key: string]: SelectedFilters;
  };
  selectedEntities: {
    [key: string]: Array<Entity>;
  };
  selectedJourneys: {
    [key: string]: Array<FilterByID>;
  };
  selectedDateRange: DateRangeWithTimezone;
  selectedDates: {
    [key: string]: DateRangeWithTimezone;
  };
  selectedMetricsType: MetricsType;
  currentJourneyAndTouchpointData: CurrentJourneyAndTouchpointBreadCrumbData | null;
  filtersOptions: FiltersOptions;
  sentFromOptions: Array<string>;
  chartFrequency: string;
  journeysColumnFilter: SelectedFilterColumnsData | null;
  touchpointColumnFilter: SelectedFilterColumnsData | null;
  peopleColumnFilter: SelectedFilterColumnsData | null;
  saveDashboardFilterList: {
    [key: string]: SavedDashboardFiltersListData;
  };
  currentDashboard: {
    [key: string]: SavedDashboardFiltersList | null;
  };
  isDashboardFiltersLoading: boolean;
  selectedDashboardEntityFilters: {
    [key: string]: SelectedDashboardEntityFilters;
  };
  isNavigationDown: boolean | null;
  isNavigationUp: boolean | null;
  selectedAllJourneysEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllTouchpointsEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllPeopleEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllJourneysFilters: {
    [key: string]: SelectedFilters;
  };
  selectedAllPeopleFilters: {
    [key: string]: SelectedFilters;
  };
  selectedAllTouchpointsFilters: {
    [key: string]: SelectedFilters;
  };
};

const initialState: AnalyticsJourneysOverviewState = {
  selectedJourneys: {},
  selectedDates: {},
  selectedDateRange: {
    startDateUTC: null,
    startDate: null,
    endDateUTC: null,
    endDate: null,
  },
  selectedMetricsType: 'overview',
  currentJourneyAndTouchpointData: null,
  filtersOptions: {
    journey_lifecycle_goal: [],
    journey_business_goal: [],
    journey_category: [],
    journey_status: [],
    recipient_type: [],
    journey_name: [],
    event_name: [],
    event_type: [],
    question_types: [],
  },
  sentFromOptions: [],
  chartFrequency: 'weekly',
  journeysColumnFilter: null,
  touchpointColumnFilter: null,
  peopleColumnFilter: null,
  saveDashboardFilterList: {},
  currentDashboard: {},
  isDashboardFiltersLoading: true,
  selectedDashboardEntityFilters: {},
  isNavigationDown: null,
  isNavigationUp: null,
  selectedAllJourneysEntities: {},
  selectedAllTouchpointsEntities: {},
  selectedAllPeopleEntities: {},
  selectedAllJourneysFilters: {},
  selectedAllPeopleFilters: {},
  selectedAllTouchpointsFilters: {},
  selectedEntities: {},
  selectedFilters: {},
};

export default (
  state: AnalyticsJourneysOverviewState = initialState,
  action: AnalyticsJourneysOverviewActions,
): AnalyticsJourneysOverviewState => {
  switch (action.type) {
    case SET_SELECTED_JOURNEYS: {
      const {dashboardKey, journeys} = action.payload;
      return {
        ...state,
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: journeys,
        },
      };
    }
    case SET_SELECTED_DATE_RANGE_AND_FREQUENCY: {
      const {chartFrequency, dashboardKey, ...dateRange} = action.payload;
      return {
        ...state,
        selectedDateRange: dateRange,
        chartFrequency,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dateRange,
        },
      };
    }
    case SET_SELECTED_DATES: {
      const {dashboardKey, dates} = action.payload;
      return {
        ...state,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dates,
        },
      };
    }
    case SET_SELECTED_METRICS_TYPE: {
      return {...state, selectedMetricsType: action.payload};
    }
    case SET_CHART_FREQUENCY: {
      return {...state, chartFrequency: action.payload};
    }
    case SET_CURRENT_JOURNEY_AND_TOUCHPOINT_DATA: {
      return {
        ...state,
        currentJourneyAndTouchpointData: {
          ...state.currentJourneyAndTouchpointData,
          ...action.payload,
        },
      };
    }
    case RECEIVE_FILTERS_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          ...action.payload,
        },
        selectedDateRange: {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
        selectedMetricsType: 'overview',
      };
    }
    case RECEIVE_SENT_FROM_OPTIONS: {
      return {
        ...state,
        sentFromOptions: action.payload,
      };
    }
    case RECEIVE_JOURNEY_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          journey_name: action.payload,
        },
      };
    }
    case RECEIVE_JOURNEY_TABLE_COLUMNS: {
      return {...state, journeysColumnFilter: action.payload};
    }
    case RECEIVE_TOUCHPOINT_TABLE_COLUMNS: {
      return {...state, touchpointColumnFilter: action.payload};
    }
    case RECEIVE_PEOPLE_TABLE_COLUMNS: {
      return {...state, peopleColumnFilter: action.payload};
    }
    case CLEAR_SELECTED_FILTERS: {
      const {dashboardKey} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: {
            journey_lifecycle_goals: [],
            journey_business_goals: [],
            journey_categories: [],
            entity_data_filter: [],
            journey_status: [],
            recipient_type: [],
            from_address: [],
            journey_ids: [],
            event_types: [],
          },
        },
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: [],
        },
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: [{id: new Date().getTime()}],
        },
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        },
      };
    }
    case SET_CURRENT_DASHBOARD: {
      const {currentDashboard, dashboardKey} = action.payload ?? {};
      const {filters} = currentDashboard ?? {};
      const {journey_ids = []}: Filters = filters ?? {};
      return {
        ...state,
        currentDashboard: {
          ...state.currentDashboard,
          [dashboardKey]: currentDashboard,
        },
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: journey_ids,
        },
      };
    }
    case RECEIVE_SAVED_DASHBOARDS: {
      const {filters, dashboard} = action.payload;
      return {
        ...state,
        saveDashboardFilterList: {
          ...state.saveDashboardFilterList,
          [dashboard]: filters,
        },
      };
    }
    case SET_LOADING_DASHBOARD_FILTERS: {
      return {
        ...state,
        isDashboardFiltersLoading: action.payload,
      };
    }

    case SET_SELECTED_DASHBOARD_ENTITY_FILTERS: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedDashboardEntityFilters: {
          ...state.selectedDashboardEntityFilters,
          [dashboardKey]: {entities},
        },
      };
    }
    case SET_NAVIGATION_DOWN: {
      return {
        ...state,
        isNavigationDown: action.payload,
      };
    }

    case SET_NAVIGATION_UP: {
      return {
        ...state,
        isNavigationUp: action.payload,
      };
    }

    case SET_ALL_JOURNEYS_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllJourneysEntities: {
          ...state.selectedAllJourneysEntities,
          [dashboardKey]: entities,
        },
      };
    }

    case SET_ALL_TOUCHPOINTS_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllTouchpointsEntities: {
          ...state.selectedAllTouchpointsEntities,
          [dashboardKey]: entities,
        },
      };
    }

    case SET_ALL_PEOPLE_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllPeopleEntities: {
          ...state.selectedAllPeopleEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_ALL_JOURNEYS_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllJourneysFilters: {
          ...state.selectedAllJourneysFilters,
          [dashboardKey]: filters,
        },
      };
    }

    case SET_ALL_PEOPLE_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllPeopleFilters: {
          ...state.selectedAllPeopleFilters,
          [dashboardKey]: filters,
        },
      };
    }

    case SET_ALL_TOUCHPOINTS_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllTouchpointsFilters: {
          ...state.selectedAllTouchpointsFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: filters,
        },
      };
    }
  }

  return state;
};
